import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import Sortable from 'react-sortablejs'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGripLines } from '@fortawesome/pro-regular-svg-icons/faGripLines'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'

import { Field, FieldArray } from 'formik'
import AsyncSelect from 'react-select/async'
import debounce from 'debounce-promise'

import { GET_LIST, useClient } from '@peracto/client'

import { Form, Group, Input } from '@peracto/peracto-ui'

import { SortableRow } from '@peracto/peracto-ui'

export const MODE_ADD = 'add'
export const MODE_EDIT = 'edit'

const AttributeGroupForm = ({ mode = MODE_EDIT, groupAttributes = [], values, ...props }) => {
    const { client } = useClient()
    const [attributes, setAttributes] = useState([])

    const fetchAttributes = async (inputValue, fieldVal) => {
        const { data } = await client(GET_LIST, 'attributes', {
            id: 'attributes',
            label: inputValue,
        })

        setAttributes(data)

        const selectedIds = fieldVal
            ? fieldVal.map(value => {
                  return value.originId ? value.originId : value.id
              })
            : []

        const values = data
            .filter(attr => !selectedIds.includes(attr.originId))
            .map(attr => ({
                label: `${attr.label} (${attr.code})`,
                value: attr.id,
            }))

        return values
    }

    const debouncedFetchAttributes = debounce(fetchAttributes, 200)

    const groupValues = {
        name: '',
        ...values,
        attributes: groupAttributes,
    }

    return (
        <Form autoComplete="off" {...props} values={groupValues}>
            <div data-testid="attribute-group-properties">
                <Group
                    key="attribute-group-properties"
                    id="attribute-group-properties"
                    name="Attribute Group Properties"
                >
                    <Input
                        name="name"
                        label="Name"
                        required
                        autoComplete="label"
                        testId="attribute-group-name"
                    />
                </Group>
            </div>

            <div data-testid="group-attributes">
                <Group key="group-attributes" id="group-attributes" name="Attributes">
                    <Field name="attributes">
                        {({ field, form }) => (
                            <>
                                <FieldArray name={field.name}>
                                    {() => (
                                        <table className="table table-sm">
                                            <colgroup>
                                                <col width="5%" />
                                                <col width="40%" />
                                                <col width="40%" />
                                                <col width="15%" />
                                            </colgroup>

                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>Label</th>
                                                    <th>Code</th>
                                                    <th className="!text-right">Remove</th>
                                                </tr>
                                            </thead>

                                            <Sortable
                                                tag="tbody"
                                                onChange={(order, sortable, evt) => {
                                                    const values = [...field.value]
                                                    const movedItem = values[evt.oldIndex]
                                                    const to = evt.newIndex
                                                    values.splice(evt.oldIndex, 1)
                                                    values.splice(to, 0, movedItem)
                                                    form.setFieldValue('attributes', values)
                                                }}
                                                options={{
                                                    animation: 100,
                                                }}
                                            >
                                                {field.value.length > 0 &&
                                                    field.value.map((item, index) => (
                                                        <SortableRow
                                                            key={item.id}
                                                            data-id={item.id}
                                                            data-testid={`attribute-row-${index}`}
                                                        >
                                                            <td>
                                                                <FontAwesomeIcon
                                                                    icon={faGripLines}
                                                                    data-testid={`attribute-${index}-slider_handle`}
                                                                />
                                                            </td>
                                                            <td
                                                                data-testid={`attribute-${index}-label`}
                                                            >
                                                                {item.label}
                                                            </td>
                                                            <td
                                                                data-testid={`attribute-${index}-code`}
                                                            >
                                                                {item.code}
                                                            </td>
                                                            <td className="!text-right">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    data-testid={`remove-attribute-${index}`}
                                                                    onClick={() => {
                                                                        const values = [
                                                                            ...field.value,
                                                                        ]
                                                                        values.splice(index, 1)
                                                                        form.setFieldValue(
                                                                            'attributes',
                                                                            [...values]
                                                                        )
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faTrash}
                                                                    />
                                                                </button>
                                                            </td>
                                                        </SortableRow>
                                                    ))}
                                            </Sortable>
                                        </table>
                                    )}
                                </FieldArray>
                                <div className="row">
                                    <div className="col-12">
                                        <div data-testid="attribute-search">
                                            <AsyncSelect
                                                className="!w-full"
                                                classNamePrefix="list"
                                                loadOptions={input =>
                                                    debouncedFetchAttributes(input, field.value)
                                                }
                                                isSearchable={true}
                                                onChange={option => {
                                                    const selectedValue = attributes.filter(
                                                        attr => attr.id == option.value
                                                    )
                                                    const fieldValues = [...field.value]
                                                    fieldValues.push(...selectedValue)
                                                    form.setFieldValue('attributes', [
                                                        ...fieldValues,
                                                    ])
                                                }}
                                                value={null}
                                                placeholder="Search for Attributes to add..."
                                                noOptionsMessage={({ inputValue }) => {
                                                    if (inputValue.length > 0) {
                                                        return `No results found for '${inputValue}'.`
                                                    } else {
                                                        return 'Enter text to begin searching.'
                                                    }
                                                }}
                                            />
                                        </div>

                                        <div className="!flex !justify-center !mt-2">
                                            <Link
                                                to="/attributes/add"
                                                className="btn btn-link"
                                                data-testid="create-attribute"
                                            >
                                                <FontAwesomeIcon
                                                    icon={faPlusCircle}
                                                    className="!mr-2"
                                                />
                                                Create Attribute
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </Field>
                </Group>
            </div>
        </Form>
    )
}

AttributeGroupForm.displayName = 'AttributeGroupForm'

AttributeGroupForm.propTypes = {
    values: PropTypes.object,
    mode: PropTypes.oneOf([MODE_ADD, MODE_EDIT]),
    schema: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
}

export default AttributeGroupForm
